/* eslint-disable @scandipwa/scandipwa-guidelines/jsx-no-props-destruction */
/*
 * @category  Puma
 * @author    Deniss Dubinins <denissd@scandiweb.com | info@scandiweb.com>
 * @license   http://opensource.org/licenses/OSL-3.0 The Open Software License 3.0 (OSL-3.0)
 * @copyright Copyright (c) 2022 Scandiweb, Inc (https://scandiweb.com)
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';

import ProductListQuery from 'Query/ProductList.query';
import { showNotification } from 'Store/Notification/Notification.action';
import { hideActiveOverlay } from 'Store/Overlay/Overlay.action';
import { ProductType } from 'Type/ProductList.type';
import { ADD_TO_CART } from 'Util/Product';
import { magentoProductTransform, transformParameters } from 'Util/Product/Transform';
import { fetchQuery } from 'Util/Request';

import SizeSelectorPopupComponent from './SizeSelectorPopup.component';
import { SIZE_SELECTOR_POPUP_ID } from './SizeSelectorPopup.config';

export const CartDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "cartDispatchers" */
    'Store/Cart/Cart.dispatcher'
);

export const WishlistDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Wishlist/Wishlist.dispatcher'
);

/** @namespace Scandipwa/Component/SizeSelectorPopup/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    payload: state.PopupReducer.popupPayload[SIZE_SELECTOR_POPUP_ID] || {},
    cartId: state.CartReducer.id
});

/** @namespace Scandipwa/Component/SizeSelectorPopup/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    hideActiveOverlay: () => dispatch(hideActiveOverlay()),
    showNotification: (type, message) => dispatch(showNotification(type, message)),
    addProduct: (options) => CartDispatcher.then(
        /** @namespace Component/AddToCart/Container/then */
        ({ default: dispatcher }) => dispatcher.addProductToCart(dispatch, options)
    ),
    removeFromWishlist: (options) => WishlistDispatcher.then(
        /** @namespace Component/SizeSelectorPopup/Container/then */
        ({ default: dispatcher }) => dispatcher.removeItemFromWishlist(dispatch, options)
    )
});

/** @namespace Scandipwa/Component/SizeSelectorPopup/Container */
export class SizeSelectorPopupContainer extends PureComponent {
    static propTypes = {
        showNotification: PropTypes.func.isRequired,
        hideActiveOverlay: PropTypes.func.isRequired,
        addProduct: PropTypes.func.isRequired,
        removeFromWishlist: PropTypes.func.isRequired,
        cartId: PropTypes.string,
        payload: PropTypes.shape({
            product: ProductType.isRequired,
            selectedColor: PropTypes.number.isRequired,
            fromWishlist: PropTypes.bool
        }).isRequired
    };

    static defaultProps = {
        cartId: ''
    };

    state = {
        isLoading: true,
        options: [],
        color_attribute_id: null,
        size_attribute_id: null,
        style_number: null
    };

    containerFunctions = {
        onOptionClick: this.onOptionClick.bind(this)
    };

    componentDidMount() {
        this.fetchAvailableOptions();
    }

    fetchAvailableOptions() {
        const {
            payload: {
                product: {
                    id
                } = {},
                selectedColor
            } = {},
            hideActiveOverlay,
            showNotification
        } = this.props;

        fetchQuery(ProductListQuery.getAvailableProductSizesByColorQuery(id, selectedColor))
            .then(
                /** @namespace Scandipwa/Component/SizeSelectorPopup/Container/SizeSelectorPopupContainer/fetchAvailableOptions/then/catch/fetchQuery/then */
                ({
                    getAvailableProductSizesByColor: {
                        size_options,
                        color_attribute_id,
                        size_attribute_id,
                        style_number
                    }
                }) => this.setState({
                    options: size_options,
                    color_attribute_id,
                    size_attribute_id,
                    style_number,
                    isLoading: false
                })
            ).catch(
                /** @namespace Scandipwa/Component/SizeSelectorPopup/Container/SizeSelectorPopupContainer/fetchAvailableOptions/then/catch */
                (error) => {
                    hideActiveOverlay();
                    // eslint-disable-next-line no-console
                    console.error(error);
                    showNotification('error', __('Something went wrong! Please, try again!'));
                }
            );
    }

    onOptionClick({ option: sizeOption }) {
        this.setState({ isLoading: true });

        const {
            payload: { product, fromWishlist },
            addProduct,
            removeFromWishlist,
            showNotification,
            hideActiveOverlay,
            cartId
        } = this.props;

        const products = this.getTransformedProduct(sizeOption);

        addProduct({
            products,
            cartId
        }).then(
            /** @namespace Scandipwa/Component/SizeSelectorPopup/Container/SizeSelectorPopupContainer/onOptionClick/addProduct/then */
            () => {
                showNotification('success', __('Product has been added to cart'));

                if (fromWishlist === true) {
                    const { wishlist: { id: item_id } } = product;

                    removeFromWishlist({ item_id, noMessages: true });
                }

                hideActiveOverlay();
            },
            /** @namespace Scandipwa/Component/SizeSelectorPopup/Container/SizeSelectorPopupContainer/onOptionClick/addProduct/then/catch */
            (error) => {
                // Do not show any error notification here, it's already triggered by dispatcher
                this.setState({ isLoading: false });
                // eslint-disable-next-line no-console
                console.error(error);
            }
        );
    }

    getTransformedProduct(sizeOption) {
        const {
            payload: { product, selectedColor }
        } = this.props;

        const {
            color_attribute_id,
            size_attribute_id,
            style_number
        } = this.state;

        if (style_number) {
            product.sku = style_number;
        }

        const parameters = {
            color: selectedColor,
            size: sizeOption
        };

        const attributes = {
            color: { attribute_id: color_attribute_id },
            size: { attribute_id: size_attribute_id }
        };

        const configurableOptions = transformParameters(parameters, attributes);

        return magentoProductTransform(
            ADD_TO_CART,
            product,
            1,
            [],
            Array.from(configurableOptions),
        );
    }

    render() {
        return (
            <SizeSelectorPopupComponent
              { ...this.props }
              { ...this.state }
              { ...this.containerFunctions }
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(SizeSelectorPopupContainer);
